import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { api, DimApiError, DimNetworkError, sessionToken, type ClientConfig } from '../api/dim';
import { deviceFingerprint } from '../capture/camera';
import { DEFAULT_THRESHOLDS, type GateThresholds } from '../capture/gates';
import { initialState, reduce, type DimEvent, type DimState } from '../fsm/machine';
import { buildReturnUrl, parseHandoff, scrubHandoffFromUrl } from '../handoff';
import { pending } from '../storage/pending';
import { CorrectScreen, DoneScreen, ExpiredScreen, FatalScreen, GuideScreen, IdentifyScreen, ManualScreen, NetworkLostScreen, ReviewScreen, WaitScreen } from '../screens/Screens';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// L'App: il riduttore puro + gli EFFETTI per fase. Ogni effetto fa una chiamata e manda un evento; mai due chiamate
// per la stessa fase (il ref `inFlight` lo impedisce anche con StrictMode). Il commit in sospeso si scrive PRIMA
// della chiamata e si cancella SOLO dopo la risposta buona.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export function App() {
  const [s, dispatch] = useReducer(reduce, undefined, initialState);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const inFlight = useRef<string | null>(null);
  const thresholds: GateThresholds = config?.gates ?? DEFAULT_THRESHOLDS;

  const send = useCallback((e: DimEvent) => dispatch(e), []);

  // Avvio: commit in sospeso → riprendi; handoff nell'URL → riscatta; sessione in questa scheda → identifica; altrimenti fatale.
  useEffect(() => {
    const p = pending.load();
    if (p && sessionToken.get()) {
      send({ type: 'RESUME_PENDING', session: p.session, candidate: p.candidate, corrections: p.corrections });
      return;
    }
    const h = parseHandoff(location.search);
    scrubHandoffFromUrl();
    if (h) {
      void api
        .redeem(h.iss, h.code, h.returnUrl, h.state)
        .then((session) => send({ type: 'REDEEMED', session }))
        .catch((e: unknown) => send({ type: 'REDEEM_FAILED', problem: e instanceof DimApiError ? { status: e.status, code: e.code, detail: e.detail } : { status: 0, code: 'network', detail: 'Server non raggiungibile' } }));
      return;
    }
    send({ type: 'FATAL', message: 'Nessun codice di avvio: apri Dim dalla voce di Quick.' });
  }, [send]);

  // La configurazione (soglie dei gate, automatico sì/no) arriva come dati appena c'è la sessione.
  useEffect(() => {
    if (!s.session) return;
    void api.config().then(setConfig).catch(() => undefined);
  }, [s.session]);

  // Lookup: mai bloccante.
  useEffect(() => {
    if (s.phase !== 'lookup' || !s.itemId) return;
    void api
      .lookupPallet(s.itemId)
      .then((item) => send({ type: 'LOOKUP_DONE', item }))
      .catch(() => send({ type: 'LOOKUP_FAILED' }));
  }, [s.phase, s.itemId, send]);

  // Computing: upload del fotogramma, candidato o rifiuto.
  useEffect(() => {
    if (s.phase !== 'computing' || !s.frame || !s.itemId) return;
    const key = `computing:${s.frame.capturedAtUtc}`;
    if (inFlight.current === key) return;
    inFlight.current = key;
    const { frame, itemId } = s;
    void deviceFingerprint()
      .then((dev) =>
        api.computeCandidate(
          {
            schemaVersion: '1.0',
            profile: 'pallet',
            itemId,
            accuracyClass: config?.defaultAccuracyClass ?? 'Operational',
            device: { fingerprint: dev.fingerprint, model: dev.model, captureWidthPx: frame.widthPx, captureHeightPx: frame.heightPx },
            calibrationProfileVersion: null,
            gates: { blurVar: frame.gates.blurVariance, clipLow: frame.gates.clipLowShare, clipHigh: frame.gates.clipHighShare, glare: frame.gates.glareShare },
            gravity: frame.gravity,
          },
          frame,
        ),
      )
      .then((candidate) => send({ type: 'CANDIDATE', candidate }))
      .catch((e: unknown) => {
        if (e instanceof DimNetworkError) send({ type: 'NETWORK_LOST' });
        else if (e instanceof DimApiError && e.status === 422) send({ type: 'REJECTED', hint: { code: e.code, hint: e.detail } });
        else if (e instanceof DimApiError && (e.status === 503 || e.status === 501)) send({ type: 'UNAVAILABLE', hint: { code: e.code, hint: `Misura automatica non disponibile (${e.detail}): inserisci a mano` } });
        else if (e instanceof DimApiError && e.status === 401) send({ type: 'FATAL', message: 'Sessione scaduta: riapri Dim da Quick' });
        else send({ type: 'FATAL', message: e instanceof Error ? e.message : String(e) });
      })
      .finally(() => {
        inFlight.current = null;
      });
  }, [s.phase, s.frame, s.itemId, config, send]);

  // Committing: record in sospeso, poi la chiamata; esattamente una volta per sessione lo garantisce il server.
  useEffect(() => {
    if (s.phase !== 'committing' || !s.candidate || !s.session) return;
    const key = `commit:${s.candidate.sessionId}`;
    if (inFlight.current === key) return;
    inFlight.current = key;
    const capturedAt = s.frame?.capturedAtUtc ?? null;
    pending.save({ session: s.session, candidate: s.candidate, corrections: s.corrections, capturedAtUtc: capturedAt });
    void api
      .commit(s.candidate.sessionId, s.corrections, capturedAt)
      .then((result) => {
        pending.clear();
        send({ type: 'COMMITTED', result });
      })
      .catch((e: unknown) => {
        if (e instanceof DimNetworkError) send({ type: 'NETWORK_LOST' });
        else if (e instanceof DimApiError && e.status === 410) {
          pending.clear();
          send({ type: 'EXPIRED' });
        } else {
          pending.clear();
          send({ type: 'FATAL', message: e instanceof DimApiError ? `${e.detail} (${e.code})` : String(e) });
        }
      })
      .finally(() => {
        inFlight.current = null;
      });
  }, [s.phase, s.candidate, s.corrections, s.session, s.frame, send]);

  const returnToQuick = s.session?.returnUrl
    ? () => {
        const url = buildReturnUrl(s.session!.returnUrl!, s.session!.state, s.result && !s.abandoned ? 'OK' : 'KO', s.result?.measurementId ?? null);
        void api.logout().finally(() => location.replace(url));
      }
    : null;

  switch (s.phase) {
    case 'redeem':
      return <WaitScreen title="Dim" label="Accesso da Quick…" />;
    case 'identify':
      return <IdentifyScreen s={s} dispatch={send} />;
    case 'lookup':
      return <WaitScreen title={s.itemId ?? 'Dim'} label="Controllo del pallet…" />;
    case 'guide':
      return <GuideScreen key={s.frame?.capturedAtUtc ?? 'guide'} s={s} thresholds={thresholds} automatic={config?.automaticEnabled ?? false} dispatch={send} />;
    case 'computing':
      return <WaitScreen title={s.itemId ?? 'Dim'} label="Misura in corso…" />;
    case 'review':
      return <ReviewScreen s={s} dispatch={send} />;
    case 'correct':
      return <CorrectScreen s={s} dispatch={send} />;
    case 'manual':
      return <ManualScreen s={s} dispatch={send} onSubmit={(v) => api.manualCandidate({ itemId: s.itemId ?? '', ...v })} />;
    case 'committing':
      return <WaitScreen title={s.itemId ?? 'Dim'} label="Salvataggio…" />;
    case 'networkLost':
      return <NetworkLostScreen dispatch={send} />;
    case 'expired':
      return <ExpiredScreen dispatch={send} />;
    case 'done':
      return <DoneScreen s={s} onReturn={returnToQuick} dispatch={send} />;
    case 'fatal':
      return <FatalScreen s={s} onReturn={returnToQuick} />;
  }
}

export type { DimState };
